import { Fragment, useEffect, useState } from "react";
import {
  ArrowDownCircleIcon,
  ArrowPathIcon,
  ArrowUpCircleIcon,
} from "@heroicons/react/20/solid";
import { ChevronDownIcon } from "@heroicons/react/16/solid";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { EllipsisHorizontalIcon } from "@heroicons/react/20/solid";
import CorrectIcon from "../../Assets/Icons/Icons";
import PendingIcon from "../../Assets/Icons/PendingIcon";
import PendingApproval from "../../Assets/Icons/PendingApproval";
import RequestForm from "../Requests/Forms/RequestForm";
import { toast, ToastContainer } from "react-toastify";
import { GetReserveAmount } from "./API/RequestsServices";
import Loader from "../../../LoadingIcons/Loader";

const statuses = {
  Paid: "text-green-700 bg-green-50 ring-green-600/20",
  Pending: "text-orange-700 bg-yellow-100 ring-red-600/10",
  Overdue: "text-red-700 bg-red-50 ring-red-600/10",
  PendingApproval: "text-blue-700 bg-blue-100 ring-red-600/10",
};

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const OldPaymentRequests = () => {
  const [isLoading, setLoading] = useState(false);
  const [isFormLoading, setFormLoading] = useState(false);
  const [ShortLink, setShortLink] = useState("");
  const [isViewLoading, setViewLoading] = useState(false);

  const [quoteNo, setQuoteNo] = useState("");
  const [debouncedQuoteNo, setDebouncedQuoteNo] = useState(quoteNo);
  const [PolicyDetails, setPolicyDetails] = useState({
    Quote: "",
    ProductName: "",
    TotalAmount: 0,
    Make: "",
    Model: "",
    Plate: "",
    desc: "",
  });

  const [CustomerDetails, setCustomerDetails] = useState({
    FirstName: "",
    lastName: "",
    email: "",
    phone: "",
    cpr: "",
    expiry: "",
  });

  const [days, setDays] = useState([]); // State to store transactions grouped by days
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1); // State for total pages
  const [totalItems, setTotalItems] = useState(0); // State for total records
  const [searchString, setSearchString] = useState(""); // State for search string
  const itemsPerPage = 7;

  const [selectedTransaction, setSelectedTransaction] = useState(null); // State for the selected transaction
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalOpenNewRequest, setIsModalOpenOpenNewRequest] = useState(false);

  useEffect(() => {
    const usernameSession = sessionStorage.getItem("username");
    if (usernameSession) {
      fetchPaymentRequests(); // Fetch the data when the component mounts or search changes
    }
  }, [currentPage]); // Only trigger fetch on page change, not search

  const fetchPaymentRequests = async () => {
    const url =
      process.env.REACT_APP_PAYMENT_API + "Requests/GetOldPaymentRequests";
    const data = {
      SearchString: searchString, // Use the search string here
      CurrentPage: currentPage,
      RowsToReturn: itemsPerPage,
      UserName: sessionStorage.getItem("username"),
    };

    try {
      setLoading(true);
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        throw new Error("Failed to fetch payment requests");
      }

      const result = await response.json();

      if (result.paymentRequests && Array.isArray(result.paymentRequests)) {
        // Handle pagination data from the API response
        setTotalPages(result.pagination.totalPages);
        setTotalItems(result.pagination.totalRecords);

        const sortedTransactions = result.paymentRequests.sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        const transactions = sortedTransactions.map((request) => ({
          id: request.key,
          amount: request.amount,
          status:
            request.isPaid && request.isSuccess
              ? "Paid"
              : request.isPaid && !request.isSuccess
              ? "Pending Approval"
              : !request.isPaid && !request.expiryDate
              ? "Pending"
              : !request.isPaid && new Date() > new Date(request.expiryDate)
              ? "Expired"
              : "Pending",
          client: `${request.customerInfo.clientName}`,
          description: request.description,
          invoiceNumber: request.referenceNumber,
          icon: null, // Placeholder for the icon component
          createdDate: request.createdDate,
          mobile: request.customerInfo.clientMobile,
          email: request.customerInfo.clientEmail,
          userName: request.createdBy,
          policyNumber: request.referenceNumber,
          expiryDate: request.expiryDate,
        }));

        setDays([{ transactions }]); // Set all transactions under a single group
        setLoading(false);
      }
    } catch (error) {
      setLoading(false);
      console.error("Error fetching payment requests:", error);
    }
  };

  const currentTransactions = days.flatMap((day) => day.transactions);

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction); // Set the selected transaction for the modal
    setIsModalOpen(true); // Open the modal
  };

  const closeModal = () => {
    setIsModalOpen(false); // Close the modal

    setShortLink("");
    setSelectedTransaction(null); // Clear the selected transaction
  };

  const OpenNewRequest = () => {
    setIsModalOpenOpenNewRequest(true); // Open the modal
  };

  const closeNewRequestModal = () => {
    setIsModalOpenOpenNewRequest(false); // Close the modal
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleSearchClick = () => {
    setCurrentPage(1); // Reset to the first page when searching
    fetchPaymentRequests(); // Call the API with the search string
  };

  const handleSearchChange = (event) => {
    setSearchString(event.target.value); // Update the search string as the user types
  };

  return (
    <div>
      {isLoading ? (
        <Loader variant="overlay" size={28} label="Fetching data..." />
      ) : (
        <div className="mt-6 overflow-hidden border-t border-gray-100">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-2xl lg:mx-0 lg:max-w-none">
              {/* Search Input */}
              <div className="mb-4 flex gap-x-2">
                <input
                  type="text"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                  placeholder="Search transactions"
                  value={searchString}
                  onChange={handleSearchChange}
                />
                <button
                  onClick={handleSearchClick}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-md"
                >
                  Search
                </button>
              </div>

              <table className="w-full text-left">
                <thead className="sr-only">
                  <tr>
                    <th>Amount</th>
                    <th className="hidden sm:table-cell">Client</th>
                    <th>More details</th>
                  </tr>
                </thead>
                <tbody>
                  {currentTransactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td className="relative py-5 pr-6">
                        <div className="flex gap-x-6">
                          {transaction.icon && (
                            <transaction.icon
                              aria-hidden="true"
                              className="hidden h-6 w-5 flex-none text-gray-400 sm:block"
                            />
                          )}
                          <div className="flex-auto">
                            <div className="flex items-start gap-x-3">
                              <div className="text-sm font-medium leading-6 text-gray-900">
                                {transaction.amount} BHD
                              </div>
                              <div
                                className={`rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                  transaction.status === "Paid"
                                    ? "text-green-700 bg-green-100 ring-green-600/20"
                                    : transaction.status === "Pending Approval"
                                    ? "text-blue-700 bg-blue-100 ring-red-600/10"
                                    : transaction.status === "Expired"
                                    ? "text-red-700 bg-red-200 ring-red-600/10"
                                    : "text-orange-700 bg-yellow-100 ring-red-600/10"
                                }`}
                              >
                                {transaction.status}
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="hidden py-5 pr-6 sm:table-cell">
                        <div className="text-sm leading-6 text-gray-900">
                          {transaction.client}
                        </div>
                        <div className="mt-1 text-xs leading-5 text-gray-500">
                          {transaction.requestType}
                        </div>
                      </td>
                      <td className="py-5 text-right">
                        <div className="flex justify-end">
                          <button
                            onClick={() => handleViewTransaction(transaction)}
                            className="text-sm font-medium leading-6 text-indigo-600 hover:text-indigo-500"
                          >
                            View
                            <span className="hidden sm:inline">
                              {" "}
                              transaction
                            </span>
                          </button>
                        </div>
                        <div className="mt-1 text-xs leading-5 text-gray-500">
                          Reference{" "}
                          <span className="text-gray-900">
                            #{" "}
                            {transaction.policyNumber ??
                              transaction.invoiceNumber}
                          </span>
                        </div>
                        <div className="mt-1 text-xs leading-5 text-gray-500">
                          Created By{" "}
                          <span className="text-gray-900 font-semibold">
                            {transaction.userName
                              ?.split(".")
                              .map((part, index) =>
                                index === 0
                                  ? part.charAt(0).toUpperCase() +
                                    part.slice(1).toLowerCase()
                                  : part.charAt(0).toUpperCase() +
                                    part.slice(1).toLowerCase()
                              )
                              .join(" ")}
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Pagination Controls */}
              <div className="flex justify-between mt-4">
                <button
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="self-center text-sm text-gray-700">
                  Page {currentPage} of {totalPages || 1}
                </span>
                <button
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal for Transaction Details */}
      {isModalOpen && selectedTransaction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <ul className="gap-x-6 gap-y-8 lg:grid-cols-3 xl:gap-x-8">
            <li className="overflow-hidden rounded-xl border border-gray-200">
              <div className="flex items-center gap-x-4 border-b border-gray-900/5 bg-gray-50 p-6">
                {selectedTransaction.status == "Paid" ? (
                  <CorrectIcon />
                ) : selectedTransaction.status == "Pending Approval" ? (
                  <PendingApproval />
                ) : (
                  <PendingIcon />
                )}

                <div className="text-sm/6 font-medium text-gray-900">
                  {selectedTransaction.policyNumber ??
                    selectedTransaction.invoiceNumber}
                </div>
                <Menu as="div" className="relative ml-auto">
                  <MenuButton className="-m-2.5 block p-2.5 text-gray-400 hover:text-gray-500">
                    <span className="sr-only">Open options</span>
                    <EllipsisHorizontalIcon
                      aria-hidden="true"
                      className="size-5"
                    />
                  </MenuButton>
                  <MenuItems
                    transition
                    className="absolute right-0 z-10 mt-0.5 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                  >
                    <MenuItem>
                      <a
                        href="#"
                        className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                      >
                        View
                        <span className="sr-only">
                          , {selectedTransaction.client}
                        </span>
                      </a>
                    </MenuItem>
                    <MenuItem>
                      <a
                        href="#"
                        className="block px-3 py-1 text-sm/6 text-gray-900 data-[focus]:bg-gray-50 data-[focus]:outline-none"
                      >
                        Edit
                        <span className="sr-only">
                          , {selectedTransaction.client}
                        </span>
                      </a>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              </div>

              <dl className="-my-3 divide-y divide-gray-100 px-6 py-4 text-sm/6 bg-white">
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Created Date</dt>
                  <dd className="text-gray-700">
                    <time dateTime={selectedTransaction.createdDate}>
                      {new Date(
                        selectedTransaction.createdDate
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                      })}
                    </time>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Amount</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      BHD {selectedTransaction.amount}
                    </div>
                    <div
                      className={classNames(
                        statuses[
                          selectedTransaction.status === "Pending Approval"
                            ? "PendingApproval"
                            : selectedTransaction.status
                        ],
                        "rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset"
                      )}
                    >
                      {selectedTransaction.status}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Name</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.client}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Phone Number</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.mobile}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Email</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.email}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Description</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.description}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Product</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.productName}
                    </div>
                  </dd>
                </div>
                <div className="flex justify-between gap-x-4 py-3">
                  <dt className="text-gray-500">Expiry Date</dt>
                  <dd className="flex items-start gap-x-2">
                    <div className="font-medium text-gray-900">
                      {selectedTransaction.expiryDate}
                    </div>
                  </dd>
                </div>
                {ShortLink == "" ? (
                  <div></div>
                ) : (
                  <div className="flex justify-between gap-x-4 py-3">
                    <dt className="text-gray-500">Link</dt>
                    <dd className="flex items-start gap-x-2">
                      <a
                        href={ShortLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-gray-900"
                      >
                        Click to view
                      </a>
                    </dd>
                  </div>
                )}

                <div className="flex justify-between gap-x-4 py-3">
                  <button
                    onClick={closeModal}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md"
                  >
                    Close
                  </button>
                </div>
              </dl>
            </li>
          </ul>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default OldPaymentRequests;
